import { useCallback, useEffect, useMemo, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Geometry, MultiPolygon, Polygon } from "@turf/helpers";
import { Theme } from "react-migration/lib/theme/Theme";
import { DeprecatedTooltip } from "react-migration/components/DeprecatedTooltip";
import { LogEventCreatedSiteEventLocation } from "react-migration/lib/util/logEventV2";
import { filterSitesInGeofence } from "react-migration/domains/geofencing/utils/filterSitesInGeofence";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { MergeSiteModal } from "../MergeSiteModal";
import { StageSelectPopoverContent, StageSelect } from "./StageSelect";
import { CreatedSite, useSaveSiteMutation } from "./useSaveSiteMutation/useSaveSiteMutation";
import { useCheckStages } from "./useCheckStages";
import { useIntersectingSitesInViewport } from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/useIntersectingSitesInViewport";
import { mergeSiteGeometries } from "react-migration/domains/sites/helpers/mergeSiteGeometries";
import { Nullable } from "src/js/types/Nullable";
import { SaveSiteLayer } from "../../typings/SaveSiteLayer";
import { Site } from "../../typings/Site";
import { SaveButton, SaveButtonState } from "./Buttons";
import { cleanTypename } from "react-migration/lib/util/cleanTypename";
import { isDefined } from "react-migration/lib/util/isDefined";
import { parseSiteGeometry } from "./utils/parseSiteGeometry";
import { ErrorPopover } from "react-migration/components/ErrorPopover";
import { isUndefined } from "lodash";
import { SiteGeometry } from "./typings/Site";
import { useOnlineStatus } from "react-migration/domains/nav/hooks/useOnlineStatus";
import { showError } from "src/js/stores/user/actions/addNotification";
import { SiteFeature } from "src/js/stores/siteGeometry/store";
import { ParsedSiteGeometry } from "./utils/parseSiteGeometry/parseSiteGeometry";
import { useUpdateSitesMutation } from "./useUpdateSitesMutation/useUpdateSitesMutation";
import { NO_SELECTION } from "./useCheckStages/useCheckStages";
import { SavedSiteButton } from "./SavedSiteButton";

const EMPTY_INTERSECTING_SITES: SiteFeature[] = [];

export function isValidSiteGeometry(geom?: Nullable<Geometry>): geom is MultiPolygon | Polygon {
  if (!geom) return false;
  if (geom.type !== "Polygon" && geom.type !== "MultiPolygon") return false;
  return true;
}

export interface SaveSiteButtonProps {
  layer: SaveSiteLayer;
  source: LogEventCreatedSiteEventLocation;
  selectionType?: string;
  text?: string;
  siteGeometry?: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.MultiPolygon;
  planning_alert_result?: string;
  handleSaveSites?: (savedSites: CreatedSite[]) => void;
  assignees?: string[];
  tidbit?: {
    planning_application: {
      ref: string;
      gss_code: string;
    };
  };
  coordId?: string;
  title?: string;
  disableDryRun?: boolean;
  siteMatchType?: "EXACT" | "INTERSECTS_POINT";
  mergeSiteOnSave?: boolean;
  isBulkSaving?: boolean;
  onCompleted?: () => void;
  fullWidth?: boolean;
  cardName?: string;
  expanded?: boolean;
}

type SaveSiteButtonInnerProps = Omit<SaveSiteButtonProps, "siteGeometry"> & {
  /**
   * Only calculated/passed if `mergeSiteOnSave` is true.
   */
  intersectingSites: SiteFeature[];
  siteGeometry: ParsedSiteGeometry;
};

/**
 * Branches based on the `mergeSiteOnSave` prop. This is a performance
 * optimisation as the `useIntersectingSitesInViewport` hook is potentially
 * expensive for accounts with many sites.
 */
export function SaveSiteButton(props: SaveSiteButtonProps) {
  const { siteGeometry: siteGeometryInput, mergeSiteOnSave } = props;
  const parsedSiteGeometry = useMemo(() => parseSiteGeometry(siteGeometryInput), [siteGeometryInput]);

  if (!mergeSiteOnSave) {
    return (
      <SaveSiteButtonInner {...props} intersectingSites={EMPTY_INTERSECTING_SITES} siteGeometry={parsedSiteGeometry} />
    );
  }

  return <SaveSiteButtonWithMergeFunctionality {...props} siteGeometry={parsedSiteGeometry} />;
}

function SaveSiteButtonWithMergeFunctionality(props: Omit<SaveSiteButtonInnerProps, "intersectingSites">) {
  const { siteGeometry } = props;
  const intersectingSites = useIntersectingSitesInViewport(siteGeometry || undefined);

  return <SaveSiteButtonInner {...props} intersectingSites={intersectingSites} />;
}

function SaveSiteButtonInner({
  handleSaveSites,
  layer,
  source,
  selectionType,
  siteGeometry,
  text,
  planning_alert_result,
  assignees,
  tidbit,
  coordId,
  title,
  disableDryRun,
  siteMatchType,
  mergeSiteOnSave = false,
  isBulkSaving = false,
  onCompleted,
  fullWidth = false,
  cardName,
  intersectingSites,
  expanded = false,
}: SaveSiteButtonInnerProps) {
  const siteToSave = useMemo(
    () => ({
      geometry: siteGeometry,
      source: { layer, planning_alert_result, title },
      assignees,
      tidbit,
      title,
      id: coordId,
    }),
    [assignees, coordId, layer, planning_alert_result, siteGeometry, tidbit, title]
  );

  /** Used in `useCheckStages` & `useSaveSiteMutation` hooks. */
  const sitesToSave = useMemo(
    () => (siteToSave.geometry ? [{ ...siteToSave, geometry: siteToSave.geometry as SiteGeometry }] : []),
    [siteToSave]
  );

  const { t } = useTranslation();
  const [isInsideGeo, setIsInsideGeo] = useState(true);
  const [showStageSelectPopover, setShowStageSelectPopover] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [hasMergeError, setHasMergeError] = useState(false);
  const isOnline = useOnlineStatus();

  const { isError, data, loading, selectedStageId, setSelectedStageId, stages, isStagesLoading, networkStatus } =
    useCheckStages({
      sites: sitesToSave,
      siteMatchType,
      disableDryRun,
    });

  const isValidStageSelected = selectedStageId && selectedStageId !== NO_SELECTION;
  const savable = useMemo(
    () => !!(isValidStageSelected && siteToSave.geometry),
    [isValidStageSelected, siteToSave.geometry]
  );

  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [updateSites, { loading: isUpdating }] = useUpdateSitesMutation();
  const [saveSite, creatingSiteState] = useSaveSiteMutation({
    source,
    selectionType,
    sites: sitesToSave,
    stageId: isValidStageSelected ? selectedStageId : undefined,
    cardName,
    onCompleted,
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (creatingSiteState.error) {
      timeout = setTimeout(() => {
        creatingSiteState.reset();
      }, 5000);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [creatingSiteState]);

  const disabled = useMemo(
    () => creatingSiteState.loading || !savable || !isInsideGeo || isUpdating,
    [creatingSiteState, savable, isInsideGeo, isUpdating]
  );

  useEffect(() => {
    const sitesInsideGeo = filterSitesInGeofence([{ geometry: siteToSave.geometry }]);
    if (!sitesInsideGeo.length) {
      setIsInsideGeo(false);
    }
  }, [siteToSave.geometry]);

  const updateExistingSiteStage = async (existingSite: Site) =>
    updateSites({
      variables: {
        siteUpdates: {
          _stage: isValidStageSelected ? selectedStageId : undefined,
          _id: existingSite?._id,
        },
      },
    });

  const saveNewSite = async () => {
    if (!isOnline) {
      showError({
        id: "offline-save-site-error",
        title: "Unable to perform action while offline.",
      });
      return;
    }

    try {
      const saveSiteResponse = await saveSite();
      const savedSites = saveSiteResponse?.data?.createSites?.map((x) => cleanTypename(x.saved_site)).filter(isDefined);

      if (!savedSites?.length) {
        throw new Error("No sites were saved");
      }

      setIsSaved(true);

      savedSites && handleSaveSites?.(savedSites);
    } catch (error) {
      console.error(error);
      setIsSaved(false);
    }
  };

  const mergeWithSite = useCallback(
    async (siteId: string) => {
      setHasMergeError(false);

      try {
        const siteToMerge = intersectingSites.find((s) => s.id === siteId);
        const siteToMergeGeometry = siteToMerge?.geometry;

        if (
          !siteToSave.geometry ||
          !siteToMerge ||
          !isValidSiteGeometry(siteToSave.geometry) ||
          !isValidSiteGeometry(siteToMergeGeometry)
        )
          return;

        const mergedSiteGeometry = mergeSiteGeometries(siteToSave.geometry, siteToMergeGeometry).geometry;

        if (!mergedSiteGeometry) throw new Error("Failed to merge site geometries.");
        const title = siteToMerge.properties?.title || undefined;

        const { data } = await updateSites({
          variables: {
            siteUpdates: {
              _id: String(siteToMerge.id),
              title,
              geometry: mergedSiteGeometry,
            },
          },
        });

        const updatedSites = data?.updateSites.map((rs) => rs.site);
        const updatedSitesWithGeometry =
          updatedSites?.map((site) =>
            site && site._id === String(siteToMerge.id) && !site.geometry
              ? { ...site, geometry: cleanTypename(mergedSiteGeometry) }
              : site
          ) ?? [];

        updatedSitesWithGeometry.length && handleSaveSites?.(updatedSitesWithGeometry);
        setIsSaved(true);
      } catch (error) {
        if (error instanceof Error) console.error(error.message);
        else console.error(`Encountered a problem merging with site (id: ${siteId}).`);
        setHasMergeError(true);

        setIsSaved(false);
      }

      setShowStageSelectPopover(false);
    },
    [handleSaveSites, intersectingSites, siteToSave.geometry, updateSites]
  );

  const onSaveHandler = () => {
    setShowStageSelectPopover(false);

    const existingSavedSite = data?.createSitesDryRun[0].saved_site;

    if (existingSavedSite) {
      updateExistingSiteStage(existingSavedSite);
    } else if (mergeSiteOnSave && intersectingSites.length) {
      setShowMergeModal(true);
    } else {
      saveNewSite();
    }
  };

  const onExpandStagePopover = useCallback(() => setShowStageSelectPopover(true), []);

  const isLoading = creatingSiteState.loading || loading || isStagesLoading;
  const isSaveable = siteToSave.geometry && !(isSaved || data?.createSitesDryRun[0].saved_site);

  const buttonState: SaveButtonState =
    siteToSave.geometry === false
      ? SaveButtonState.ERROR
      : isLoading || isBulkSaving
      ? SaveButtonState.LOADING
      : isSaveable
      ? SaveButtonState.IDLE
      : SaveButtonState.SAVED;

  if (isUndefined(siteToSave.geometry) || isError) return null;

  if (buttonState === SaveButtonState.SAVED && data?.createSitesDryRun[0]?.saved_site) {
    return <SavedSiteButton site={data?.createSitesDryRun[0]?.saved_site} />;
  }

  // Inline expanded mode - show dropdowns and save button directly
  if (expanded) {
    return (
      <>
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-col overflow-hidden gap-y-1.5 grow" data-testid="stage-select">
            <p className="text-sm text-gray-600 m-0!">Select a stage</p>
            <StageSelect
              value={selectedStageId}
              onChange={setSelectedStageId}
              stages={stages}
              loading={isStagesLoading}
              networkStatus={networkStatus}
              withStageColor={true}
              alwaysDropdown={true}
            />
          </div>
          <div className="flex justify-end">
            <SaveButton buttonState={buttonState} onSave={onSaveHandler} copy={{ save: text }} expanded={true} />
          </div>
        </div>
        {showMergeModal && (
          <MergeSiteModal
            disabled={disabled}
            intersectingSites={intersectingSites}
            handleCancelSave={() => setShowMergeModal(false)}
            handleSaveAsSiteAs={saveNewSite}
            handleMergeSave={mergeWithSite}
            hasError={hasMergeError}
            setHasError={setHasMergeError}
          />
        )}
      </>
    );
  }

  // Default popover mode
  return (
    <>
      <DeprecatedTooltip
        title={
          isInsideGeo
            ? t("sites.save_site.save_site_dropdown.save")
            : t("sites.save_site.save_site.search_results_outside_of_geofence")
        }
        placement="bottom"
        theme={Theme.Dark}
        disabled={isInsideGeo}
      >
        <Popover.Root open={showStageSelectPopover} onOpenChange={setShowStageSelectPopover}>
          <Popover.Anchor>
            <SaveButton
              buttonState={buttonState}
              onSave={onSaveHandler}
              copy={{ save: text }}
              onExpand={onExpandStagePopover}
              fullWidth={fullWidth}
            />
          </Popover.Anchor>

          <Popover.Portal>
            <Popover.Content
              className="z-30 flex flex-col w-screen p-4 bg-white border rounded-sm md:w-60 gap-y-4 border-neutral-100 drop-shadow-xl"
              align="center"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <StageSelectPopoverContent
                setSelectedStageId={setSelectedStageId}
                selectedStageId={selectedStageId}
                stages={stages}
                loading={isStagesLoading}
                networkStatus={networkStatus}
                saveHandler={onSaveHandler}
                closeHandler={() => setShowStageSelectPopover(false)}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
        <ErrorPopover.Root open={!!creatingSiteState.error}>
          <ErrorPopover.Anchor />
          <ErrorPopover.Portal>
            <ErrorPopover.Content variant="ERROR" side="bottom" align="end">
              <ErrorPopover.Header>{t("sites.save_site.error_header")}</ErrorPopover.Header>
              <ErrorPopover.Body>{t("sites.save_site.error_body")}</ErrorPopover.Body>
            </ErrorPopover.Content>
          </ErrorPopover.Portal>
        </ErrorPopover.Root>
      </DeprecatedTooltip>
      {showMergeModal && (
        <MergeSiteModal
          disabled={disabled}
          intersectingSites={intersectingSites}
          handleCancelSave={() => setShowMergeModal(false)}
          handleSaveAsSiteAs={saveNewSite}
          handleMergeSave={mergeWithSite}
          hasError={hasMergeError}
          setHasError={setHasMergeError}
        />
      )}
    </>
  );
}
